// src/components/prospecting-contacts/ProspectingContactDetails.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import StateDropdown from "../../../shared/forms/StateProvinceDropDown";
import CountryDropdown from "../../../shared/forms/CountriesDropDown";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  Divider,
  Paper,
  InputAdornment,
  LinearProgress,
} from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

import BackButton from "../../../shared/components/BackButton";
import { PRIMARY_BUTTON_SX } from "../../../theme/buttonstyles";
import { useAuth } from "../../../providers/AuthProvider";
import api, { isCancel } from "../../../services/api/axios";
import { addRecentItem } from "../../home-dashboard/RecentItemsHelper";
import CompanyAutocomplete from "../../../shared/components/CompanyAutoComplete";
import {
  buildProspectingCompanySelection,
  buildProspectingContactPayload,
  EMPTY_PROSPECTING_CONTACT,
  mapProspectingContactToForm,
  mergeLocationIfEmpty,
} from "./ProspectingContactDetailsHelpers";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";

const hasValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return String(value ?? "").trim().length > 0;
};

const getSectionCompletion = (record, fields) => {
  const completed = fields.filter((field) =>
    hasValue(field.getValue ? field.getValue(record) : record[field.key]),
  ).length;
  return { completed, total: fields.length };
};

function SectionHeader({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
      <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: BRAND_GREEN, flexShrink: 0 }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE }}>
        {children}
      </Typography>
    </Box>
  );
}

export default function ProspectingContactDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id;
  const prospectingCompanyId = searchParams.get("company_id");
  const { user } = useAuth();

  const [contact, setContact] = useState(EMPTY_PROSPECTING_CONTACT);
  const [loading, setLoading] = useState(!isNew);

  const contactCompletion = React.useMemo(() => {
    const sections = {
      general: getSectionCompletion(contact, [
        { key: "name" },
        { getValue: (item) => item.company?.name },
        { key: "job_title" },
        { key: "notes" },
      ]),
      contactInfo: getSectionCompletion(contact, [
        { key: "email" },
        { key: "work_phone" },
        { key: "mobile_phone" },
        { key: "linkedin" },
      ]),
      location: getSectionCompletion(contact, [
        { key: "country" },
        { key: "state" },
        { key: "city" },
      ]),
    };
    const completed = Object.values(sections).reduce(
      (sum, section) => sum + section.completed,
      0,
    );
    const total = Object.values(sections).reduce(
      (sum, section) => sum + section.total,
      0,
    );

    return {
      ...sections,
      completed,
      total,
      overall: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [contact]);

  useEffect(() => {
    if (isNew) { setLoading(false); return; }

    const controller = new AbortController();
    api
      .get(`/prospecting-contacts/${id}`, { signal: controller.signal })
      .then((res) => setContact(mapProspectingContactToForm(res.data || {})))
      .catch((error) => { if (!isCancel(error)) alert("Failed to load prospecting contact."); })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [id, isNew]);

  useEffect(() => {
    if (!isNew || !prospectingCompanyId || contact.company?.id) return;

    const controller = new AbortController();
    api
      .get(`/prospecting-companies/${prospectingCompanyId}`, { signal: controller.signal })
      .then((res) => {
        const company = res.data || {};
        setContact((prev) => mergeLocationIfEmpty({ ...prev, company: buildProspectingCompanySelection(company) }, company));
      })
      .catch((error) => { if (!isCancel(error)) alert("Failed to load prospecting company."); });

    return () => controller.abort();
  }, [contact.company?.id, isNew, prospectingCompanyId]);

  useEffect(() => {
    if (!contact.company?.id) return;

    const controller = new AbortController();
    api
      .get(`/prospecting-companies/${contact.company.id}`, { signal: controller.signal })
      .then((res) => setContact((prev) => mergeLocationIfEmpty(prev, res.data || {})))
      .catch(() => {});

    return () => controller.abort();
  }, [contact.company?.id]);

  useEffect(() => {
    if (!loading && !isNew && user?.id && contact.name) {
      addRecentItem(user.id, "prospecting_contact", { id, name: contact.name });
    }
  }, [loading, isNew, id, contact.name, user?.id]);

  const handleSubmit = () => {
    if (!contact.company) { alert("Prospecting company is required."); return; }

    const payload = buildProspectingContactPayload(contact);
    const req = isNew
      ? api.post("/prospecting-contacts/", payload)
      : api.patch(`/prospecting-contacts/${id}`, payload);

    req
      .then(() => navigate("/prospecting-contacts"))
      .catch(() => alert("Failed to save contact."));
  };

  if (loading) return (
    <Box sx={{ p: 4, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <Typography sx={{ color: BRAND_BLUE, fontWeight: 600 }}>Loading...</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 2, minHeight: "100%", boxSizing: "border-box", overflowX: "hidden", bgcolor: "#f0f4f8" }}>
      <Box sx={{ maxWidth: 1850, mx: "auto", width: "100%" }}>

        {/* HEADER CARD */}
        <Paper elevation={0} sx={{ mb: 2, borderRadius: 3, overflow: "hidden", border: "1px solid rgba(0,51,108,0.10)", boxShadow: "0 2px 12px rgba(0,51,108,0.08)", bgcolor: "white" }}>
          <Box sx={{ height: 4, background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_GREEN})` }} />

          <Box sx={{ px: 3, pt: 2.5, pb: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
              <BackButton to="/prospecting-contacts" />
              {user?.organization?.name && (
                <Chip
                  label={user.organization.name}
                  size="small"
                  sx={{ bgcolor: "rgba(0,51,108,0.06)", color: BRAND_BLUE, fontWeight: 600, fontSize: 12, border: "1px solid rgba(0,51,108,0.12)" }}
                />
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ width: 46, height: 46, borderRadius: 2, bgcolor: "rgba(0,51,108,0.07)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <PersonIcon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ color: BRAND_BLUE, fontWeight: 700, lineHeight: 1.2 }}>
                  {isNew ? "New Prospect Contact" : contact.name || "Untitled Contact"}
                </Typography>
                {!isNew && contact.company?.name && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}>
                    <BusinessIcon sx={{ fontSize: 14, color: BRAND_GREEN }} />
                    <Typography variant="body2" sx={{ color: BRAND_GREEN }}>
                      {contact.company.name}
                    </Typography>
                  </Box>
                )}
                {!isNew && contact.job_title && (
                  <Typography variant="caption" sx={{ color: BRAND_GREEN }}>
                    {contact.job_title}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Divider />
        </Paper>

        {/* CONTENT CARD */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid rgba(0,51,108,0.10)", boxShadow: "0 2px 12px rgba(0,51,108,0.08)", p: 3, bgcolor: "white" }}>
          <Box
            sx={{
              mb: 2.5,
              p: { xs: 2, md: 2.5 },
              borderRadius: 2,
              border: "1px solid rgba(0,51,108,0.06)",
              bgcolor: "rgba(0,51,108,0.025)",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
              <Box>
                <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 900, lineHeight: 1.15 }}>
                  Contact Profile
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  {contactCompletion.completed} of {contactCompletion.total} fields completed
                </Typography>
              </Box>
              <Chip
                label={`${contactCompletion.overall}% Complete`}
                sx={{
                  bgcolor: "rgba(100,131,21,0.12)",
                  color: BRAND_GREEN,
                  border: "1px solid rgba(100,131,21,0.25)",
                  fontWeight: 900,
                }}
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={contactCompletion.overall}
              sx={{
                height: 8,
                borderRadius: 999,
                bgcolor: "rgba(0,51,108,0.10)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 999,
                  bgcolor: BRAND_GREEN,
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" },
              gap: 2,
            }}
          >
            {/* General */}
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
              <SectionHeader>General</SectionHeader>
              <Stack spacing={3}>
                <TextField
                  label="Name"
                  value={contact.name}
                  onChange={(e) => setContact((prev) => ({ ...prev, name: e.target.value }))}
                  fullWidth
                />
                <CompanyAutocomplete
                  source="prospecting"
                  selectedCompany={contact.company ? { id: contact.company.id, source: "prospecting" } : null}
                  onCompanySelect={(c) =>
                    setContact((prev) => ({
                      ...prev,
                      company: c ? { id: c.id, name: c.name, source: "prospecting" } : null,
                    }))
                  }
                />
                <TextField
                  label="Job Title"
                  value={contact.job_title}
                  onChange={(e) => setContact((prev) => ({ ...prev, job_title: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Notes"
                  multiline
                  minRows={5}
                  value={contact.notes}
                  onChange={(e) => setContact((prev) => ({ ...prev, notes: e.target.value }))}
                  inputProps={{ maxLength: 5000 }}
                  helperText={`${contact.notes?.length ?? 0}/5000`}
                  fullWidth
                />
              </Stack>
            </Box>

            {/* Contact Info */}
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
              <SectionHeader>Contact Info</SectionHeader>
              <Stack spacing={3}>
                <TextField
                  label="Email"
                  value={contact.email}
                  onChange={(e) => setContact((prev) => ({ ...prev, email: e.target.value }))}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: BRAND_GREEN, fontSize: 20 }} /></InputAdornment>,
                  }}
                />
                <TextField
                  label="Work Phone"
                  value={contact.work_phone}
                  onChange={(e) => setContact((prev) => ({ ...prev, work_phone: e.target.value }))}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: BRAND_BLUE, fontSize: 20 }} /></InputAdornment>,
                  }}
                />
                <TextField
                  label="Mobile Phone"
                  value={contact.mobile_phone}
                  onChange={(e) => setContact((prev) => ({ ...prev, mobile_phone: e.target.value }))}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PhoneIphoneIcon sx={{ color: BRAND_BLUE, fontSize: 20 }} /></InputAdornment>,
                  }}
                />
                <TextField
                  label="LinkedIn"
                  value={contact.linkedin}
                  onChange={(e) => setContact((prev) => ({ ...prev, linkedin: e.target.value }))}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LinkedInIcon sx={{ color: "#0A66C2", fontSize: 20 }} /></InputAdornment>,
                  }}
                />
              </Stack>
            </Box>

            {/* Location */}
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
              <SectionHeader>Location</SectionHeader>
              <Stack spacing={3}>
                <CountryDropdown
                  label="Country"
                  value={contact.country}
                  onChange={(e) => setContact((prev) => ({ ...prev, country: e.target.value }))}
                  fullWidth
                />
                <StateDropdown
                  label="State / Province"
                  country={contact.country}
                  value={contact.state}
                  onChange={(e) => setContact((prev) => ({ ...prev, state: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="City"
                  value={contact.city}
                  onChange={(e) => setContact((prev) => ({ ...prev, city: e.target.value }))}
                  fullWidth
                />
              </Stack>
            </Box>
          </Box>

          {/* Save button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, pt: 2.5, borderTop: "1px solid rgba(0,51,108,0.08)" }}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} sx={PRIMARY_BUTTON_SX}>
              {isNew ? "Add Contact" : "Save Changes"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
